/**
 * Role Play Setup (CRM web) — pick avatars + difficulty + playbook.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, Play, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTIES = [
    { k: 'friendly', l: 'Friendly', sub: 'Avatars cooperate', color: '#1DA063' },
    { k: 'standard', l: 'Standard', sub: 'Realistic responses', color: '#3B82F6' },
    { k: 'hard', l: 'Hard', sub: 'Objections & resistance', color: '#F2A423' },
    { k: 'nightmare', l: 'Nightmare', sub: 'Tough, distracted, hostile', color: '#E5484D' },
];

const DISC_COLORS = { D: '#E5484D', I: '#F2A423', S: '#1DA063', C: '#3B82F6' };

const ROLE_COLOR = {
    'Decision maker': '#5D3FD3',
    'Champion': '#1DA063',
    'Economic buyer': '#4527A0',
    'Influencer': '#3B82F6',
    'Technical buyer': '#0891B2',
    'End user': '#85858F',
    'Blocker': '#E5484D',
    'Unknown': '#B8B8BE',
};

export default function RolePlaySetup() {
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [avatars, setAvatars] = useState([]);
    const [playbooks, setPlaybooks] = useState([]);
    const [selected, setSelected] = useState([]);
    const [difficulty, setDifficulty] = useState('standard');
    const [playbookId, setPlaybookId] = useState('');
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [av, pb] = await Promise.all([
                    axios.get(`${API}/role-play/avatars`, { headers: getAuthHeader() }),
                    axios.get(`${API}/playbooks`, { headers: getAuthHeader() }).catch(() => ({ data: { playbooks: [] } })),
                ]);
                const list = av.data?.avatars || [];
                setAvatars(list);
                setSelected(list.slice(0, 2).map(a => a.avatar_id));
                const pblist = pb.data?.playbooks || pb.data || [];
                setPlaybooks(pblist);
            } catch (e) {
                toast.error('Failed to load role play setup');
            } finally {
                setLoading(false);
            }
        })();
    }, [getAuthHeader]);

    const start = async () => {
        if (!selected.length) {
            toast.error('Pick at least one AI avatar');
            return;
        }
        setStarting(true);
        try {
            const res = await axios.post(`${API}/role-play/sessions`, {
                avatar_ids: selected,
                playbook_id: playbookId || null,
                difficulty,
            }, { headers: getAuthHeader() });
            navigate(`/practice/role-play/live/${res.data.session_id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to start session');
            setStarting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6 pb-32">
            <div
                className="rounded-2xl p-8 mb-8 text-white"
                style={{ background: 'linear-gradient(135deg, #2d1a6e, #1a0f3d)' }}
            >
                <div className="text-xs font-bold tracking-widest uppercase opacity-70 mb-2">AI Role Play</div>
                <h1 className="text-4xl font-semibold" style={{ fontFamily: '"Instrument Serif", serif' }}>
                    Rehearse before the real call.
                </h1>
                <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    Pick AI avatars to play your prospects. They'll respond in persona — then you get a 6-pillar coaching debrief.
                </p>
            </div>

            <Step n={1} title="Select AI avatars" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                {avatars.map(a => {
                    const on = selected.includes(a.avatar_id);
                    return (
                        <button
                            key={a.avatar_id}
                            onClick={() => setSelected(on ? selected.filter(x => x !== a.avatar_id) : [...selected, a.avatar_id])}
                            className={`flex items-start gap-3 p-4 rounded-xl text-left border-2 transition-all ${on ? 'bg-violet-50 border-violet-500' : 'bg-white border-stone-200 hover:border-stone-300'}`}
                        >
                            <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                                style={{ background: `linear-gradient(135deg, ${a.avatar_color}, ${a.avatar_color}88)` }}
                            >{a.avatar_initials}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold">{a.name}</span>
                                    {on && <CheckCircle2 className="w-4 h-4 text-violet-600" />}
                                </div>
                                <div className="text-xs text-stone-500">{a.title} · {a.company}</div>
                                <div className="text-xs italic mt-1" style={{ color: '#4527A0' }}>
                                    "{a.persona_summary}"
                                </div>
                                <div className="flex gap-1.5 mt-2 flex-wrap">
                                    {a.disc && (
                                        <span
                                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                                            style={{ background: DISC_COLORS[a.disc.primary] }}
                                        >{a.disc.primary}</span>
                                    )}
                                    <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                        style={{
                                            background: (ROLE_COLOR[a.buying_role] || '#85858F') + '20',
                                            color: ROLE_COLOR[a.buying_role] || '#85858F',
                                        }}
                                    >{a.buying_role}</span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <Step n={2} title="Difficulty" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8">
                {DIFFICULTIES.map(d => (
                    <button
                        key={d.k}
                        onClick={() => setDifficulty(d.k)}
                        className={`p-3 rounded-xl text-left border-2 transition-all ${difficulty === d.k ? 'bg-white' : 'bg-stone-50 border-stone-200'}`}
                        style={difficulty === d.k ? { borderColor: d.color } : {}}
                    >
                        <div className="text-sm font-bold mb-0.5" style={{ color: d.color }}>{d.l}</div>
                        <div className="text-[10px] text-stone-500 leading-snug">{d.sub}</div>
                    </button>
                ))}
            </div>

            <Step n={3} title="Playbook" />
            <div className="bg-white border border-stone-200 rounded-xl p-4 mb-4">
                <select
                    value={playbookId}
                    onChange={e => setPlaybookId(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-stone-200 bg-white text-sm"
                >
                    <option value="">Free-form (no playbook)</option>
                    {playbooks.map(p => (
                        <option key={p.playbook_id} value={p.playbook_id}>
                            {p.name} {p.win_rate != null && `· ${Math.round(p.win_rate * 100)}% win rate`}
                        </option>
                    ))}
                </select>
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur border-t border-stone-200 p-4 pl-[280px]">
                <div className="max-w-5xl mx-auto">
                    <button
                        onClick={start}
                        disabled={starting || !selected.length}
                        className="w-full h-12 rounded-xl bg-stone-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-stone-800 disabled:opacity-50"
                    >
                        <Play className="w-4 h-4" />
                        {starting ? 'Starting…' : `Start role play · ${selected.length} avatar${selected.length !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Step({ n, title }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ background: '#111113' }}
            >{n}</span>
            <h2 className="text-xs font-bold tracking-widest uppercase text-stone-700">{title}</h2>
        </div>
    );
}
